import * as React from 'react';
import {
  Grid,
  Card,
  CardHeader,
  CardContent,
  Box,
  Paper,
  Divider,
  Button,
  Typography,
  Icon,
  CircularProgress,
  IconButton,
} from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import { useSnackbar } from 'notistack';
import {
  Edit,
} from '@material-ui/icons';
import {
  makeStyles, createStyles,
} from '@material-ui/core/styles';
import StoreDetails from './StoreDetails';
import StoreOverview from './StoreOverview';
import type { IStoreDetails } from './StoreInterfaces';
import { colors } from '../../../theme';
import StoreTags from './StoreTags';
import StoreDetailsOptions from './StoreDetailsOptions';

const useStyles = makeStyles(() => createStyles({
  backgroundBlue: {
    background: colors.primary,
  },
  textBlue: {
    color: colors.primary,
  },
  filterBtn: {
    background: colors.primary,
    color: colors.white,
  },
}));

interface IEditStoreProps {
  editingHidden: boolean;
  isFetching?: boolean;
  backToAllStores?: () => void;
  handleSaveBtnClicked?: () => void;
  setStoreFormValues: (values: IStoreDetails) => void;
  StoreFormValues: IStoreDetails;
}

const EditStore: React.FC<IEditStoreProps> = ({
  editingHidden, isFetching, backToAllStores, handleSaveBtnClicked, setStoreFormValues, StoreFormValues,
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const handleAddNewTag = () => {
    if (StoreFormValues.tags.some((tag) => tag.key === '' || tag.value === '')) {
      enqueueSnackbar('Cannot add new tag, while previous tags are not filled out', { variant: 'error' });
    } else {
      const temp = [...StoreFormValues.tags];
      temp.push({
        inheritable: false,
        key: '',
        value: '',
      });
      setStoreFormValues({ ...StoreFormValues, tags: temp });
    }
  };

  const handleRemoveTag = (index: number) => {
    const temp = [...StoreFormValues.tags];
    temp.splice(index, 1);
    setStoreFormValues({ ...StoreFormValues, tags: temp });
  };

  return (
    <>
      <Grid container spacing={0}>
        <Grid item xs={12}>
          <Box py={1}>
            {/* Hide if on add Store page. */}
            {!editingHidden && (
              <>
                <Grid container xs={4}>
                  <Grid item>
                    <Box p={1}>
                      <Icon>
                        <Edit
                          color="primary"
                        />
                      </Icon>
                    </Box>
                  </Grid>
                  <Grid item>
                    <Box p={1}>
                      <Typography variant="h6" className={classes.textBlue} display="inline">{'Edit Store'}</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </>
            )}
          </Box>

          <Box py={3}>
            <Card>
              <Paper>
                <CardHeader title="Store Overview" />
                <Divider variant="fullWidth" />
                <CardContent>
                  <StoreOverview
                    formValues={StoreFormValues}
                    setFormValues={setStoreFormValues}
                  />
                </CardContent>
              </Paper>
            </Card>
          </Box>
          <Box>
            <Card>
              <Paper>
                <CardHeader title="Assign Tags" />
                <Divider variant="fullWidth" />
                <CardContent>
                  <StoreTags
                    formValues={StoreFormValues}
                    setFormValues={setStoreFormValues}
                    handleRemove={handleRemoveTag}
                  />
                  <Grid item xs={12}>
                    <Box px={1} clone>
                      <Button
                        variant="text"
                        color="primary"
                        onClick={() => { handleAddNewTag(); }}
                        style={{ minWidth: '5.625rem' }}
                      >
                        <IconButton disableFocusRipple disabled disableRipple>
                          <AddCircleIcon style={{ color: '#6CAE75', float: 'right' }} fontSize="large" />
                        </IconButton>
                        {'ADD TAG'}
                      </Button>
                    </Box>
                  </Grid>
                </CardContent>
              </Paper>
            </Card>
          </Box>
          <Box py={3}>
            <Card>
              <Paper>
                <CardHeader title="Store Details" />
                <Divider variant="fullWidth" />
                <CardContent>
                  <StoreDetailsOptions
                    formValues={StoreFormValues}
                    setFormValues={setStoreFormValues}
                  />
                </CardContent>
              </Paper>
            </Card>
          </Box>
          <Box py={3}>
            <Card>
              <Paper>
                <CardHeader title="Store Details" />
                <Divider variant="fullWidth" />
                <CardContent>
                  <StoreDetails
                    formValues={StoreFormValues}
                    setFormValues={setStoreFormValues}
                  />
                </CardContent>
              </Paper>
            </Card>
            <Grid item xs={12} container spacing={2} direction="row-reverse">
              <Box pt={3}>
                <Button onClick={backToAllStores}>
                  <Typography className={classes.textBlue} display="inline">{'BACK TO ALL Stores'}</Typography>
                </Button>
                <Button disabled={isFetching || StoreFormValues.tags.some((t) => t.inherited === true && t.value === '')} color="primary" variant="contained" onClick={handleSaveBtnClicked}>
                  {isFetching ? <CircularProgress size={25} />
                    : 'SAVE CHANGES'}
                </Button>
              </Box>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default EditStore;
